import { FC } from 'react';
import ReactGA4 from 'react-ga4';
import { NumberUtils, Tr } from '@utils';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { ElectrificationWizard } from '@services/electrification-wizard';

const EMAIL_SUBJECT = 'Electrification Wizard Suggestions';

const toPriceString = (price?: number) => NumberUtils.formatAsPrice(price, { pureDefault: true });
const getEmailBody = (electrificationWizard?: ElectrificationWizard) =>
	[
		`I would like to talk about the electrification wizard result below. (WizardId: ${electrificationWizard?.id})`,
		'\n',
		'Vehicles:',
		...(electrificationWizard?.simulationTasks.map(
			({ fleetVehicle: { plateNumber, id } = {}, id: simulationId }) =>
				`\t${plateNumber || ''} (VehicleId: ${id} / SimulationTaskId: ${simulationId})`
		) || []),
		'\n',
		'Packages:',
		...(electrificationWizard?.packageDealsCalculated.map(
			({ packageDeal: { id, name }, packageDealId }) =>
				`\t${name} (PackageId: ${id}${
					packageDealId === electrificationWizard.selectedPackageDealCalculatedId ? ' / Selected' : ''
				})`
		) || []),
		'\n',
		`TCO Price Per Month: ${toPriceString(electrificationWizard?.tcoPricePerMonth)}`,
		`Leasing Price Per Month: ${toPriceString(electrificationWizard?.leasingPerMonth)}`
	]
		.filter(Boolean)
		.join('\n');

export interface IWizardSuggestionFooterProps {
	electrificationWizard?: ElectrificationWizard;
	contactPersons: string[];
	leasingPerMonth: number;
	showPrices?: boolean;
}

export const WizardSuggestionFooter: FC<IWizardSuggestionFooterProps> = ({
	electrificationWizard,
	contactPersons,
	leasingPerMonth,
	showPrices
}) => {
	const { tcoPricePerMonth, packageDealsCalculated, selectedPackageDealCalculatedId } = electrificationWizard || {};
	const selectedCalculated = packageDealsCalculated?.find(({ id }) => id === selectedPackageDealCalculatedId);
	const tcoPriceStr = toPriceString(tcoPricePerMonth);
	const leasingPriceStr = toPriceString(leasingPerMonth);

	const onTalkWithUsButtonClick = () => {
		ReactGA4.event({
			category: 'Electrification Wizard',
			action: 'Electrification Wizard - Talk With Us Click',
			label: `WizardId_${electrificationWizard?.id}`
		});
		if (contactPersons?.length) {
			const to = contactPersons[0];
			const cc = contactPersons.slice(1);
			const link = document.createElement('a');
			const query = [
				`subject=${encodeURIComponent(EMAIL_SUBJECT)}`,
				`body=${encodeURIComponent(getEmailBody(electrificationWizard))}`,
				cc.length ? `cc=${cc.join(',')}` : undefined
			].filter(Boolean);
			link.href = `mailto:${to}?${query.join('&')}`;
			document.body.appendChild(link);
			link.click();
			link.remove();
		}
	};

	return (
		<Paper elevation={8} square sx={{ display: 'grid', minHeight: 140, paddingX: 9.5, paddingY: 3.5 }}>
			<Stack direction="row" spacing={8}>
				<Grid container direction="row" sx={{ flexGrow: 1, flexWrap: 'nowrap' }}>
					{showPrices ? (
						<>
							<Grid item xs={6} sx={{ paddingRight: 5 }}>
								<Stack direction="column" spacing={0.5}>
									<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
										{`${selectedCalculated?.packageDeal.name}: ${tcoPriceStr}`}
									</Typography>
									<Typography>
										<Tr.Portal path="wizard.suggestions.tco-description" />
									</Typography>
								</Stack>
							</Grid>
							<Grid item xs={6} sx={{ paddingLeft: 5 }}>
								<Stack direction="column" spacing={0.5}>
									<Typography variant="h5" sx={{ fontWeight: 'bold' }}>
										<Tr.Portal path="wizard.suggestions.leasing-price-title" options={{ price: leasingPriceStr }} />
									</Typography>
									<Typography>
										<Tr.Portal path="wizard.suggestions.leasing-price-description" />
									</Typography>
								</Stack>
							</Grid>
						</>
					) : null}
				</Grid>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginY: 'auto' }}>
					<Button
						className="electrification-wizard-talk-with-us" // GTM tag trigger
						variant="contained"
						color="secondary"
						sx={{ minWidth: 190, textTransform: 'uppercase' }}
						onClick={onTalkWithUsButtonClick}
						data-testid="wizard-suggestion-footer-contact-button"
					>
						<Tr.Portal path="wizard.suggestions.talk-with-us" />
					</Button>
				</Box>
			</Stack>
		</Paper>
	);
};
